// Stamp formatting shared by the comments thread and the likers list. Both
// contracts hand back a localized "YYYY-MM-DD HH:MM" string (Rails :datetime
// format) for persisted records, or an ISO8601 stamp for optimistic nodes, so
// the parsing quirks below are handled once here. Each caller picks its own
// presentation - the design asks for an absolute date on top-level comments, a
// relative time on replies, and either one on a like depending on its age. Intl
// keeps them locale-aware without per-locale strings. `now` is injectable
// throughout so the time-dependent formatters are deterministic under test.
const UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
]

const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/

const toDate = (value) => {
  if (!value) {
    return null
  }

  // Rails serializes "YYYY-MM-DD HH:MM"; the space separator is rejected by
  // Safari/Firefox, so promote it to "YYYY-MM-DDTHH:MM".
  let normalized = String(value).trim().replace(' ', 'T')

  // That string carries no timezone, yet the app zone is UTC - so parsing it as
  // browser-local would drift the result by the viewer's offset (a fresh reply
  // rendering as "in 3 hours"). Pin zone-less values to UTC. Optimistic nodes
  // and any offset-bearing stamps already end in Z / +hh:mm and pass through.
  if (ZONELESS_DATETIME.test(normalized)) {
    normalized += 'Z'
  }

  const date = new Date(normalized)

  return Number.isNaN(date.getTime()) ? null : date
}

export const formatAbsoluteDate = (iso, locale = 'en') => {
  const date = toDate(iso)

  if (!date) {
    return ''
  }

  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}

// English fallback only - a guard so a missing key renders "30 min" rather than
// "30 undefined". Callers are expected to pass a localized `formatUnit`; the
// locale bundles carry a key per unit.
const FALLBACK_UNIT_LABELS = {
  year: 'y',
  month: 'mo',
  week: 'w',
  day: 'd',
  hour: 'h',
  minute: 'min',
  second: 's',
}

// Compact elapsed time to match Figma ("30 min", "1 h") - no "ago" suffix, which
// is why Intl.RelativeTimeFormat cannot be used here (every style appends one:
// "1 hr ago", "1 時間前"). `formatUnit(unit, amount)` supplies the localized
// string, so the caller's i18n owns both the abbreviation and its spacing - CJK
// locales set no space ("30分") where European ones do ("30 min"). Always
// past-facing: the delta is clamped to >= 0. `now` is injectable for tests.
export const formatRelativeTime = (iso, { formatUnit, now = Date.now() } = {}) => {
  const date = toDate(iso)

  if (!date) {
    return ''
  }

  const deltaSeconds = Math.max(0, Math.round((now - date.getTime()) / 1000))
  const [unit, secondsInUnit] = UNITS.find(([, seconds]) => deltaSeconds >= seconds) || ['second', 1]
  const amount = Math.max(1, Math.floor(deltaSeconds / secondsInUnit))

  return formatUnit ? formatUnit(unit, amount) : `${amount} ${FALLBACK_UNIT_LABELS[unit]}`
}

// Short absolute date for stamps older than the relative window. The year is
// appended only when it differs from the current one: "20 Dec" read in July of
// the following year parses as a date five months in the future, while
// "20 Dec 2026" cannot be misread. Both years are read in the viewer's zone so
// the comparison matches the string Intl actually renders. `now` is injectable
// for tests.
export const formatShortDate = (iso, locale = 'en', { now = Date.now() } = {}) => {
  const date = toDate(iso)

  if (!date) {
    return ''
  }

  const isCurrentYear = date.getFullYear() === new Date(now).getFullYear()

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(isCurrentYear ? {} : { year: 'numeric' }),
  }).format(date)
}

// Seconds since `iso`, clamped to the past. Lets a caller pick between a
// relative label and an absolute date without re-implementing the zone-less
// Rails parsing above. Null for an unparseable stamp, so neither branch runs.
export const elapsedSeconds = (iso, now = Date.now()) => {
  const date = toDate(iso)

  return date ? Math.max(0, Math.round((now - date.getTime()) / 1000)) : null
}
